<template>
	<div :class="{'extra-padding': !allowPageChange}">
		<small v-if="totalResults && activePage"><b>{{ pageStart }}</b> - <b>{{ pageEnd }}</b> of <b>{{ totalResults }}</b> results</small>
		<b class="margin-left-5 margin-right-5" v-if="allowPageChange">|</b>
		<small v-if="allowPageChange">Show</small>
		<dropdown v-if="allowPageChange">
			<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
				{{ resultsPerPage }}
				<i class="fa fa-angle-down"></i>
			</button>
			<ul slot="dropdown-menu" class="dropdown-menu">
				<li v-for="result in resultOptions">
					<a @click="updateResultsPerPage(result)">{{ result }}</a>
				</li>
			</ul>
		</dropdown>
		<small v-if="allowPageChange">Results Per Page</small>
	</div>
</template>

<script>
import Dropdown from './Dropdown'

/**
 * Page Results module is used to display the dropdown to select the number of records to show per page as well as to show the current page results number out of the total results number
 * @class PageResults
 * @prop {integer} totalResults - Used to determine the total number of results
 * @prop {integer} activePage - Used to determine what the current active page is
 * @prop {boolean} allowPageChange - Used to allow or prevent a user from changing the number of results on a page
 * @prop {integer} resultsPerPageOverride - Used when the page results do not pull from the generic page results variable
 * @since 0.0.3
 */

/**
 * Create a variable that will hold the default number of results.
 * @var {integer}
 * @memberof PageResults
 */
const defaultResults = 25

export default {
	name: 'page-results',
	props: {
		totalResults: {
			default: 0
		},
		activePage: {
			default: 1
		},
		allowPageChange: {
			default: true
		},
		resultsPerPageOverride: {
			default: null
		}
	},
	data () {
		return {
			resultsPerPage: defaultResults,
			resultOptions: [25, 50, 100]
		}
	},
	/**
	 * Run on `created` to retrieve the users preference from localStorage or to set it as the default value. Also let the parent know that the results have been set.
	 * @function
	 * @returns {undefined}
	 * @memberof PageResults
	 * @version 0.0.4
	 */
	created () {
		if (this.checkLocalStorage()) {
			this.resultsPerPage = parseInt(this.checkLocalStorage())
		} else {
			this.setLocalStorage(defaultResults)
		}
		this.emitResults()
	},
	computed: {
		/**
		 * Computed to determine the start point for the current page that is displaying.
		 * @function
		 * @returns {integer} - An integer representing the start point for the current page.
		 * @memberof PageResults
	 	 * @version 0.0.4
		 */
		pageStart () {
			let localPageStart = 0
			let pageResultsAmount = this.resultsPerPageOverride ? this.resultsPerPageOverride : this.resultsPerPage
			if (this.activePage) {
				localPageStart = (this.activePage - 1) * pageResultsAmount + 1
			}
			return localPageStart
		},
		/**
		 * Computed to determine the end point for the current page that is displaying.
		 * @function
		 * @returns {integer} - An integer representing the end point for the current page.
		 * @memberof PageResults
	 	 * @version 0.0.4
		 */
		pageEnd () {
			let localPageEnd = 0
			let pageResultsAmount = this.resultsPerPageOverride ? this.resultsPerPageOverride : this.resultsPerPage
			if (this.activePage) {
				localPageEnd = this.activePage * pageResultsAmount
			}
			if (localPageEnd > this.totalResults) {
				localPageEnd = this.totalResults
			}
			return localPageEnd
		}
	},
	methods: {
		/**
		 * To check localStorage to see if the user has set a preference.
		 * @function
		 * @returns {integer} - An integer that indicates the number of results the user has set as their preference or 0 to indicate that the preference is not set.
		 * @memberof PageResults
	 	 * @version 0.0.4
		 */
		checkLocalStorage () {
			/* eslint-disable no-undef */
			if (localStorage.getItem('pageResults')) {
				return localStorage.getItem('pageResults')
			} else {
				return 0
			}
		},
		/**
		 * To set the localStorage to hold the users preference for page results.
		 * @function
		 * @param {integer} results - An integer that represents the number of results to show per page.
		 * @returns {undefined}
		 * @memberof PageResults
	 	 * @version 0.0.4
		 */
		setLocalStorage (results) {
			localStorage.setItem('pageResults', results)
		},
		/**
		 * To set the users selection as the active page results which will then update localStorage and inform the parent.
		 * @function
		 * @param {integer} results - An integer that represents the number of results to show per page.
		 * @returns {undefined}
		 * @memberof PageResults
	 	 * @version 0.0.4
		 */
		updateResultsPerPage (results) {
			if (this.resultsPerPage === results) {
				return
			}

			this.resultsPerPage = results
			this.setLocalStorage(results)
			this.emitResults()
		},
		/**
		 * To emit to the parent what the number of results are in order to pull in the appropriate number of results.
		 * @function
		 * @returns {undefined}
		 * @memberof PageResults
	 	 * @version 0.0.4
		 */
		emitResults () {
			this.$emit('pageResults', this.resultsPerPage)
		}
	},
	components: {
		Dropdown
	}
}
</script>
<style>
.extra-padding {
	padding: 5px 0;
}
</style>



// WEBPACK FOOTER //
// PageResults.vue?057973dc